import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Product } from '../types';
import ProductCard from './ProductCard';
import { getWindowOpenAt, getWindowCloseAt, getWindowState } from '../utils/productWindows';

interface FragmentsPageProps {
  onProductClick?: (product: Product) => void;
}

// The catalogue — the complete fragment record. Open windows claimable,
// upcoming ones marked, closed ones present but clearly closed.
// Order: open first, then upcoming (soonest first), then closed
// (most recently closed first).
const FragmentsPage: React.FC<FragmentsPageProps> = ({ onProductClick }) => {
  const [fragments, setFragments] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Dynamic page header (CMS-overridable via page_content/fragments)
  const [pageContent, setPageContent] = useState({
    title: 'FRAGMENTS',
    subtitle: 'Recovered graphics, reissued once.',
    heroImage: '/assets/placeholder.svg'
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [productsSnap, contentSnap] = await Promise.all([
          getDocs(query(collection(db, 'products'), where('tier', '==', 'fragment'))),
          (async () => {
            const { doc, getDoc } = await import('firebase/firestore');
            return getDoc(doc(db, 'page_content', 'fragments'));
          })()
        ]);

        if (contentSnap.exists()) {
          const data = contentSnap.data();
          setPageContent(prev => ({
            title: data.title || prev.title,
            subtitle: data.subtitle || prev.subtitle,
            heroImage: data.heroImage || prev.heroImage
          }));
        }

        const now = new Date();
        const rank = (p: Product) => {
          const state = getWindowState(p, now);
          return state === 'open' ? 0 : state === 'upcoming' ? 1 : 2;
        };

        const list: Product[] = [];
        productsSnap.forEach((doc) => {
          list.push({ id: doc.id, ...doc.data() } as Product);
        });

        list.sort((a, b) => {
          const rankDiff = rank(a) - rank(b);
          if (rankDiff !== 0) return rankDiff;
          const state = getWindowState(a, now);
          if (state === 'upcoming') {
            // Soonest opening first
            return (getWindowOpenAt(a) || '').localeCompare(getWindowOpenAt(b) || '');
          }
          if (state === 'closed') {
            // Most recently closed first
            return (getWindowCloseAt(b) || '').localeCompare(getWindowCloseAt(a) || '');
          }
          // Open: soonest closing first
          return (getWindowCloseAt(a) || '').localeCompare(getWindowCloseAt(b) || '');
        });

        setFragments(list);
      } catch (error) {
        console.error('Error fetching fragments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Header */}
      <div className="relative h-[200px] md:h-[250px] lg:h-[300px] bg-gray-900 overflow-hidden">
        <img
          src={pageContent.heroImage}
          alt=""
          aria-hidden="true"
          onError={(e) => {
            const t = e.currentTarget;
            if (!t.dataset.fallback) { t.dataset.fallback = '1'; t.src = '/assets/placeholder.svg'; }
          }}
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-normal tracking-widest uppercase mb-4">{pageContent.title}</h1>
          {pageContent.subtitle && (
            <p className="font-heading text-base md:text-lg tracking-widest">{pageContent.subtitle}</p>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="min-h-[40vh] flex items-center justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
          </div>
        ) : fragments.length === 0 ? (
          <div className="py-20 text-center">
            <p className="font-mono text-sm text-gray-500 tracking-wider">The archive is being assembled.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {fragments.map(product => (
              <ProductCard key={product.id} product={product} onClick={onProductClick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FragmentsPage;
